import React, { useState } from "react";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
} from "antd";
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { payrollAmountInputProps } from "./payrollAmountInput";
import dayjs from "dayjs";

const money = (value) => `UGX ${Number(value || 0).toLocaleString("en-UG")}`;

const PayrollSalaryScalesTab = ({
  scales,
  saving,
  saveScale,
  deleteScale,
  copyVersion,
}) => {
  const [form] = Form.useForm();
  const [copyForm] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [copySource, setCopySource] = useState(null);

  const showForm = (scale = null) => {
    setSelected(scale);
    form.resetFields();
    form.setFieldsValue(
      scale
        ? {
            ...scale,
            stepCode: scale.stepCode || scale.code,
            effectiveFrom: scale.effectiveFrom
              ? dayjs(scale.effectiveFrom)
              : null,
            effectiveTo: scale.effectiveTo ? dayjs(scale.effectiveTo) : null,
            steps: scale.steps?.map((step) => ({ ...step })),
          }
        : {
            isActive: true,
            effectiveFrom: dayjs(),
            steps: [{ name: "Step 1", basicSalary: null, isActive: true }],
          },
    );
    setOpen(true);
  };

  const submit = async () => {
    const values = await form.validateFields();
    const payload = {
      ...values,
      effectiveFrom: values.effectiveFrom?.format("YYYY-MM-DD") || null,
      effectiveTo: values.effectiveTo?.format("YYYY-MM-DD") || null,
      steps: values.steps.map((step, index) => ({
        ...step,
        sortOrder: index + 1,
      })),
    };
    const result = await saveScale(selected?.scaleId, payload);
    if (result) setOpen(false);
  };

  const submitCopy = async () => {
    const values = await copyForm.validateFields();
    const result = await copyVersion(copySource.scaleId, {
      effectiveFrom: values.effectiveFrom.format("YYYY-MM-DD"),
      effectiveTo: values.effectiveTo?.format("YYYY-MM-DD") || null,
    });
    if (result) setCopySource(null);
  };

  const columns = [
    {
      title: "Salary scale",
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <strong>{row.name}</strong>
          <span style={{ color: "#8c8c8c" }}>
            Scale code: {row.code} · Step code: {row.stepCode || row.code} · v
            {row.versionNumber}
          </span>
        </Space>
      ),
    },
    {
      title: "Employee category",
      dataIndex: "employeeCategory",
      render: (value) => value || "All categories",
    },
    {
      title: "Effective",
      render: (_, row) =>
        `${row.effectiveFrom ? dayjs(row.effectiveFrom).format("DD MMM YYYY") : "Any date"} — ${row.effectiveTo ? dayjs(row.effectiveTo).format("DD MMM YYYY") : "Open"}`,
    },
    {
      title: "Salary steps",
      render: (_, row) => (
        <Space wrap>
          {row.steps
            ?.filter((step) => step.isActive)
            .map((step) => (
              <Tag key={step.stepId}>
                {step.name}: {money(step.basicSalary)}
              </Tag>
            ))}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      render: (value) => (
        <Tag color={value ? "success" : "default"}>
          {value ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      width: 130,
      align: "center",
      render: (_, row) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => showForm(row)}
            />
          </Tooltip>
          <Tooltip title="Copy as a new version">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => {
                setCopySource(row);
                copyForm.resetFields();
                copyForm.setFieldsValue({
                  effectiveFrom: dayjs().add(1, "day"),
                });
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this salary scale?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => deleteScale(row.scaleId)}
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={saving}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <div style={{ textAlign: "right" }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => showForm()}
        >
          Add salary scale
        </Button>
      </div>
      <Table
        rowKey="scaleId"
        columns={columns}
        dataSource={scales}
        scroll={{ x: 800 }}
      />

      <Modal
        title={selected ? "Edit salary scale" : "Add salary scale"}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={submit}
        confirmLoading={saving}
        okText="Save scale"
        width={850}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label="Scale name"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="code"
                label="Scale code"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="employeeCategory" label="Employee category">
                <Select
                  allowClear
                  options={["Teaching", "Support", "Administration"].map(
                    (value) => ({ value, label: value }),
                  )}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="effectiveFrom" label="Effective from">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="effectiveTo" label="Effective to">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="isActive" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="description" label="Description">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ marginBottom: 12 }}>
            <strong>Levels / steps</strong>
            <div style={{ color: "#8c8c8c" }}>
              Add each level under this family. Employees are assigned one step,
              and its amount supplies the standard basic salary.
            </div>
          </div>
          <Form.Item
            name="stepCode"
            label="Step code"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.List name="steps">
            {(fields, { add, remove }) => (
              <Space direction="vertical" style={{ width: "100%" }}>
                {fields.map((field, index) => (
                  <Row gutter={12} align="middle" key={field.key}>
                    <Form.Item name={[field.name, "stepId"]} hidden>
                      <Input />
                    </Form.Item>
                    <Col xs={24} md={9}>
                      <Form.Item
                        name={[field.name, "name"]}
                        label="Step name"
                        rules={[{ required: true }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={20} md={10}>
                      <Form.Item
                        name={[field.name, "basicSalary"]}
                        label="Monthly basic salary (UGX)"
                        rules={[{ required: true }]}
                      >
                        <InputNumber
                          {...payrollAmountInputProps}
                          min={1}
                          precision={2}
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={4} md={2}>
                      <Form.Item
                        name={[field.name, "isActive"]}
                        label="Active"
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={2}>
                      <Button
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        disabled={fields.length === 1}
                        onClick={() => remove(field.name)}
                      />
                    </Col>
                  </Row>
                ))}
                <Button
                  type="dashed"
                  block
                  icon={<PlusOutlined />}
                  onClick={() => add({ isActive: true })}
                >
                  Add salary step
                </Button>
              </Space>
            )}
          </Form.List>
        </Form>
      </Modal>
      <Modal
        title={
          copySource
            ? `Copy ${copySource.code} v${copySource.versionNumber}`
            : "Copy scale"
        }
        open={Boolean(copySource)}
        onCancel={() => setCopySource(null)}
        onOk={submitCopy}
        confirmLoading={saving}
        okText="Create new version"
      >
        <Form form={copyForm} layout="vertical">
          <Form.Item
            name="effectiveFrom"
            label="New version effective from"
            rules={[{ required: true }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="effectiveTo" label="New version effective to">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default PayrollSalaryScalesTab;
